const RED = "#DC2626";

const DimLine = ({ x1, y1, x2, y2, text, offset = -18 }) => {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * offset, ny = (dx / len) * offset;
  const a = [x1 + nx, y1 + ny], b = [x2 + nx, y2 + ny];
  const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  const t = 4;
  return (
    <g pointerEvents="none">
      <line x1={x1} y1={y1} x2={a[0]} y2={a[1]} stroke={RED} strokeWidth="0.6" strokeDasharray="2 2" />
      <line x1={x2} y1={y2} x2={b[0]} y2={b[1]} stroke={RED} strokeWidth="0.6" strokeDasharray="2 2" />
      <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={RED} strokeWidth="1" />
      <line x1={a[0] - nx / Math.abs(offset) * t} y1={a[1] - ny / Math.abs(offset) * t} x2={a[0] + nx / Math.abs(offset) * t} y2={a[1] + ny / Math.abs(offset) * t} stroke={RED} strokeWidth="1.2" />
      <line x1={b[0] - nx / Math.abs(offset) * t} y1={b[1] - ny / Math.abs(offset) * t} x2={b[0] + nx / Math.abs(offset) * t} y2={b[1] + ny / Math.abs(offset) * t} stroke={RED} strokeWidth="1.2" />
      <g transform={`translate(${mx} ${my}) rotate(${angle > 90 || angle < -90 ? angle + 180 : angle})`}>
        <rect x={-text.length * 3.6 - 4} y={-8} width={text.length * 7.2 + 8} height={14} fill="white" opacity="0.9" />
        <text textAnchor="middle" y="3" fontSize="11" fontFamily="'JetBrains Mono', monospace" fill={RED} fontWeight="600">{text}</text>
      </g>
    </g>
  );
};

const PipePath = ({ d, w, selected }) => (
  <>
    <path data-export-hide d={d} fill="none" stroke="transparent" strokeWidth={w + 24} />
    <path d={d} fill="none" stroke={selected ? RED : "#27272A"} strokeWidth={w + 3} strokeLinejoin="round" strokeLinecap="butt" />
    <path d={d} fill="none" stroke="#CBD5E1" strokeWidth={w} strokeLinejoin="round" strokeLinecap="butt" />
    <path d={d} fill="none" stroke="#F8FAFC" strokeWidth={Math.max(1, w * 0.25)} strokeLinejoin="round" opacity="0.8" />
  </>
);

export const ShapeRenderer = ({ shape, scale, selected, showDims }) => {
  const s = scale;
  const d = shape.dims;
  const w = Math.max(4, (d.diameter || 0) * s);
  let body = null, dims = null, label = [0, -30];

  if (shape.type === "pipe") {
    const L = d.length * s;
    body = <PipePath d={`M0 0 H${L}`} w={w} selected={selected} />;
    dims = <><DimLine x1={0} y1={-w / 2} x2={L} y2={-w / 2} text={`${d.length}`} /><text x={L + 8} y={4} fontSize="10" fontFamily="'JetBrains Mono', monospace" fill="#64748B">{`Ø${d.diameter}`}</text></>;
    label = [L / 2, w / 2 + 16];
  } else if (shape.type === "bend") {
    const A = d.legA * s, B = d.legB * s;
    body = <PipePath d={`M0 0 H${A} V${B}`} w={w} selected={selected} />;
    dims = <><DimLine x1={0} y1={-w / 2} x2={A + w / 2} y2={-w / 2} text={`${d.legA}`} /><DimLine x1={A + w / 2} y1={0} x2={A + w / 2} y2={B} text={`${d.legB}`} /><text x={4} y={w / 2 + 14} fontSize="10" fontFamily="'JetBrains Mono', monospace" fill="#64748B">{`Ø${d.diameter}`}</text></>;
    label = [A / 2, w / 2 + 30];
  } else if (shape.type === "tee") {
    const L = d.length * s, Br = d.branch * s;
    body = <><PipePath d={`M${L / 2} 0 V${Br}`} w={w} selected={selected} /><PipePath d={`M0 0 H${L}`} w={w} selected={selected} /></>;
    dims = <><DimLine x1={0} y1={-w / 2} x2={L} y2={-w / 2} text={`${d.length}`} /><DimLine x1={L / 2 + w / 2} y1={0} x2={L / 2 + w / 2} y2={Br} text={`${d.branch}`} offset={-22} /><text x={L + 8} y={4} fontSize="10" fontFamily="'JetBrains Mono', monospace" fill="#64748B">{`Ø${d.diameter}`}</text></>;
    label = [L / 4, w / 2 + 16];
  } else if (shape.type === "rect") {
    const W = d.width * s, H = d.height * s;
    body = <rect x={0} y={0} width={W} height={H} fill="#F1F5F9" fillOpacity="0.85" stroke={selected ? RED : "#27272A"} strokeWidth={selected ? 2.5 : 1.5} />;
    dims = <><DimLine x1={0} y1={0} x2={W} y2={0} text={`${d.width}`} /><DimLine x1={0} y1={H} x2={0} y2={0} text={`${d.height}`} /></>;
    label = [W / 2, H / 2 + 4];
  } else if (shape.type === "circle") {
    const R = (d.diameter * s) / 2;
    body = <><circle cx={0} cy={0} r={R} fill="#F1F5F9" fillOpacity="0.85" stroke={selected ? RED : "#27272A"} strokeWidth={selected ? 2.5 : 1.5} /><line x1={-R} y1={0} x2={R} y2={0} stroke="#94A3B8" strokeDasharray="4 3" /></>;
    dims = <DimLine x1={-R} y1={-R} x2={R} y2={-R} text={`Ø${d.diameter}`} offset={-10} />;
    label = [0, 16];
  }

  return (
    <g>
      {body}
      {showDims && dims}
      {shape.label && <text x={label[0]} y={label[1]} textAnchor="middle" fontSize="12" fontWeight="700" fontFamily="'Barlow', sans-serif" fill="#18181B" pointerEvents="none">{shape.label}</text>}
    </g>
  );
};
